import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Suggestion,
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { Loader2, Lightbulb, RefreshCw } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { generateJsonStream } from "@/utils/ai/utils";
import { getContextFromFeaturePrd } from "../EngSpec/utils";
import {
  fixJson,
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
} from "@/utils/utils";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { FeaturePRD } from "@/types/issues";
export const SuggestionsButton = React.memo(
  ({
    onSelectSuggestion,
  }: {
    onSelectSuggestion: (suggestion: Suggestion) => void;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [fetchingSuggestions, setFetchingSuggestions] = useState(false);
    const canvasId = useCanvasId();
    const { session } = useSession();
    const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);
    const featurePrd = useIssuesStore(
      (state) => state.canvasIdToIssue[canvasId]?.featurePrd,
    );

    const { suggestions, setSuggestions } = useAiContextStore((state) => ({
      suggestions: state.aiChatSuggestions,
      setSuggestions: state.setAiChatSuggestions,
    }));

    const isChatLoading = useIsChatLoading();

    const fetchSuggestions = async () => {
      if (fetchingSuggestions) return;

      setFetchingSuggestions(true);

      try {
        const { contextWithImages, userSelectionContext } =
          getContextFromFeaturePrd(featurePrd ?? undefined);
        const designSuggestions = featurePrd?.design_suggestions;

        const body = {
          feature_title: featurePrd?.title || "",
          feature_prd: featurePrd?.prd || "",
          visual_context: contextWithImages,
          user_selection_context: JSON.stringify(userSelectionContext),
          mermaid_diagram: featurePrd?.mermaid_diagram || "",
          ui_design_scaffolding_spec: JSON.stringify(designSuggestions),
        };

        const supabaseJWT = await getUnexpiredToken(session);

        const stream = await generateJsonStream(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/ai/vercel-ai/aiChatSuggestions`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          },
        );

        for await (const chunk of stream) {
          if (chunk.status !== -1 || chunk.message !== "intermediate") {
            const finalText = chunk.data.steps[0].text;
            const resJson = fixJson(finalText).fixedObj;
            setSuggestions(resJson?.suggestions || []);
            break;
          }
        }
      } catch (e) {
        logAxiosError("Error generating chat suggestions", e);
        toast.error(
          `Error generating chat suggestions: ${getAxiosErorrMessage(e)}`,
        );
      } finally {
        setFetchingSuggestions(false);
        refreshPlanInfo(session);
      }
    };

    return (
      <DropdownMenu open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            data-tooltip-id="tooltip"
            // TODO: make this actually appear on top :(
            data-tooltip-place="top"
            data-tooltip-content={`Suggestions`}
            disabled={isChatLoading}
            className={cn(
              "h-[25px] w-[30px] p-0 rounded-none transition-colors border border-secondary-n-900",
            )}
            style={{
              borderWidth: "1px 1px 0px 1px",
              borderStyle: "solid",
              borderTopLeftRadius: "var(--radius-md)",
              borderTopRightRadius: "var(--radius-md)",
              borderColor: "var(--secondary-n-900)",
            }}
          >
            <Lightbulb
              className={cn(
                "h-3.5 w-3.5",
                "text-icons-secondary",
                isOpen ? "text-secondary-n-50" : "text-icons-secondary",
              )}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="max-w-60 rounded-lg"
          side="top"
        >
          <DropdownMenuLabel className="p-1 w-full flex flex-row items-center justify-between text-sm gap-2">
            <div className="w-full">
              {suggestions?.length ? (
                <div className="w-full flex flex-row items-center justify-between gap-2">
                  <p className="text-secondary-n-50 text-xs font-medium">
                    Choose a suggestion
                  </p>
                  <Button
                    className="p-0 px-1 hover:bg-secondary-n-1000"
                    variant="ghost"
                    size="xs"
                    onClick={fetchSuggestions}
                  >
                    <RefreshCw
                      className={cn(
                        "w-4 h-4 text-secondary-n-600",
                        fetchingSuggestions && "animate-spin",
                      )}
                    />
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-secondary-n-400 text-center text-xs font-medium px-2">
                    Let AI recommend the next steps for your project.
                  </p>
                  <Button
                    onClick={fetchSuggestions}
                    className="w-full mt-2 flex justify-center items-center text-xs text-secondary-n-50 font-medium  gap-1 rounded-md bg-secondary-n-1000 hover:border-none border-secondary-300 "
                    variant="ghost"
                    style={{
                      borderStyle: "Solid",
                      borderWidth: "1px",
                      borderColor: "var(--secondary-n-900)",
                    }}
                  >
                    {fetchingSuggestions ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      "Get suggestions"
                    )}
                  </Button>
                </div>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator
            className={cn(!suggestions?.length && "hidden")}
          />
          <div className={cn("relative", !suggestions?.length && "hidden")}>
            <div
              className={cn(
                "max-h-40 overflow-y-scroll hide-scrollbar px-1 py-1",
                fetchingSuggestions && "opacity-10 pointer-events-none",
              )}
            >
              {suggestions?.map((suggestion, index) => (
                <DropdownMenuItem
                  key={index}
                  className="text-xs text-secondary-n-50 hover:bg-secondary-n-800 hover:text-white transition-colors duration-300 rounded-md"
                  onClick={() => onSelectSuggestion(suggestion)}
                >
                  {suggestion.short_title}
                </DropdownMenuItem>
              ))}
            </div>
            <div
              className={cn(
                "absolute left-[calc(50%-0.75rem)] top-[calc(50%-0.75rem)] ",
                !fetchingSuggestions && "hidden",
              )}
            >
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);
